import React, { useState, useEffect } from 'react';
import { transferAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const TransferRequests = () => {
  const { user } = useAuth();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [processing, setProcessing] = useState(null);
  
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    loadTransfers();
  }, []);

  const loadTransfers = async () => {
    try {
      setLoading(true);
      const response = await transferAPI.getMyTransfers();
      setTransfers(response.data);
    } catch (error) {
      console.error('Error loading transfers:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to load transfer requests' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (transferId) => {
    if (!window.confirm('Accept this device assignment?')) return;
    
    setProcessing(transferId);
    setMessage({ type: '', text: '' });
    
    try {
      await transferAPI.approveTransfer(transferId);
      setMessage({ type: 'success', text: 'Transfer request approved successfully.' });
      loadTransfers();
    } catch (error) {
      console.error('Error approving transfer:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to approve transfer' 
      });
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (transferId, isForCurrentUser) => {
    const action = isSuperAdmin && !isForCurrentUser ? 'cancel' : 'reject';
    if (!window.confirm(`${action === 'cancel' ? 'Cancel' : 'Reject'} this device assignment?`)) return;
    
    setProcessing(transferId);
    setMessage({ type: '', text: '' });
    
    try {
      await transferAPI.rejectTransfer(transferId);
      setMessage({ type: 'success', text: `Assignment request ${action === 'cancel' ? 'cancelled' : 'rejected'}.` });
      loadTransfers();
    } catch (error) {
      console.error('Error rejecting transfer:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || `Failed to ${action} transfer` 
      });
    } finally {
      setProcessing(null);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="card">
        <h2>Assignment Requests</h2>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>Assignment Requests</h2>

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      {transfers.length === 0 ? (
        <p>No pending assignment requests</p>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Asset ID</th>
                <th>Device</th>
                <th>From</th>
                <th>To</th>
                <th>Delivery Unit</th>
                <th>Requested Date & Time</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {transfers.map((transfer) => (
                <tr key={transfer.id} style={{ backgroundColor: transfer.isForCurrentUser ? '#fffbeb' : 'transparent' }}>
                  <td style={{ fontWeight: '600' }}>{transfer.deviceId}</td>
                  <td>
                    <div>{transfer.deviceName}</div>
                    <div style={{ fontSize: '0.8125rem', color: '#64748b' }}>{transfer.deviceModel}</div>
                  </td>
                  <td>{transfer.fromEmployeeName}</td>
                  <td>
                    {transfer.isForCurrentUser ? (
                      <strong style={{ color: '#667eea' }}>You</strong>
                    ) : (
                      transfer.toEmployeeName
                    )}
                  </td>
                  <td>{transfer.deliveryUnit || 'N/A'}</td>
                  <td>{formatDateTime(transfer.requestedAt)}</td>
                  <td>{transfer.notes || '-'}</td>
                  <td>
                    {transfer.isForCurrentUser ? (
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-primary"
                          onClick={() => handleApprove(transfer.id)}
                          disabled={processing === transfer.id}
                          style={{ 
                            padding: '0.5rem 0.75rem',
                            fontSize: '0.8125rem',
                            minWidth: '80px'
                          }}
                        >
                          {processing === transfer.id ? (
                            <><i className="fas fa-spinner fa-spin"></i></>
                          ) : (
                            <><i className="fas fa-check"></i> Accept</>
                          )}
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleReject(transfer.id, transfer.isForCurrentUser)}
                          disabled={processing === transfer.id}
                          style={{ 
                            padding: '0.5rem 0.75rem',
                            fontSize: '0.8125rem',
                            backgroundColor: '#dc2626',
                            borderColor: '#dc2626',
                            minWidth: '80px'
                          }}
                        >
                          {processing === transfer.id ? (
                            <><i className="fas fa-spinner fa-spin"></i></>
                          ) : (
                            <><i className="fas fa-times"></i> Reject</>
                          )}
                        </button>
                      </div>
                    ) : isSuperAdmin ? (
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleReject(transfer.id, transfer.isForCurrentUser)}
                        disabled={processing === transfer.id}
                        style={{ 
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.8125rem',
                          backgroundColor: '#dc2626',
                          borderColor: '#dc2626',
                          minWidth: '80px'
                        }}
                      >
                        {processing === transfer.id ? (
                          <><i className="fas fa-spinner fa-spin"></i></>
                        ) : (
                          <><i className="fas fa-ban"></i> Cancel</>
                        )}
                      </button>
                    ) : (
                      <span style={{ 
                        padding: '0.25rem 0.75rem',
                        backgroundColor: '#e0e7ff',
                        color: '#667eea',
                        borderRadius: '4px',
                        fontSize: '0.8125rem',
                        fontWeight: 600
                      }}>
                        Pending
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TransferRequests;
